import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../../lib/session";
import { apiErrorOf } from "../../lib/api";
import {
  trackFapsCodeRedeemed,
  trackFapsPillOpened,
  trackFapsWallClicked,
  useFaps,
} from "../../lib/faps";
import { AuthModal } from "../components/AuthModal";
import { CrownMark } from "../ui/Crown";
import { FapsAmount } from "../ui/FapsAmount";

/**
 * /faps/redeem?code=… — the ONLY way a bought pack is claimed now (the owner's call,
 * 2026-09-08: redemption is by link, and the wallet's paste-a-code form is gone). A
 * purchase on Patreon is fulfilled by the operator messaging the buyer this link; landing
 * it takes ZERO presses — the page reads the code from the URL and redeems it on sight.
 *
 * Self-contained rather than the wallet dialog: there is no redeem form to open anymore.
 * Signed out, it asks for an account and keeps the code in state across the sign-in; the
 * redeem then fires on its own the moment there is a user. The code is read ONCE and the
 * URL is replaced without it, so it never sits in history or a copied address (`code` is
 * already scrubbed from analytics URLs). Source `link` so the funnel tells a delivered
 * code from anything else.
 */
export function RedeemLink() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { user, redeem } = useFaps();
  const sessionReady = useSession((s) => s.ready);
  const [code] = useState(() => new URLSearchParams(search).get("code") ?? "");
  const [authOpen, setAuthOpen] = useState(false);
  const [state, setState] = useState<
    { t: "idle" } | { t: "busy" } | { t: "done"; amount: number } | { t: "error"; message: string }
  >({ t: "idle" });
  // A code is single-use: redeem EXACTLY once, even through StrictMode's double effect and
  // a re-render after sign-in. The ref latches before the request so the second pass bails.
  const started = useRef(false);

  useEffect(() => {
    trackFapsPillOpened("link");
    if (search) navigate(pathname, { replace: true });
    // Once, on arrival — the code is already in state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sessionReady || !user || !code || started.current) return;
    started.current = true;
    setState({ t: "busy" });
    void (async () => {
      try {
        const r = await redeem(code);
        trackFapsCodeRedeemed(r.amount);
        setState({ t: "done", amount: r.amount });
      } catch (e) {
        setState({
          t: "error",
          message: (apiErrorOf(e)?.error ?? "Couldn't redeem that — try again.").replace(/^\d+:\s*/, ""),
        });
      }
    })();
  }, [sessionReady, user, code, redeem]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-6 text-center shadow-2xl">
        <div className="mb-3 flex justify-center" style={{ color: "var(--crown-gold, #f5c518)" }}>
          <CrownMark size={40} />
        </div>

        {!code ? (
          <>
            <h1 className="mb-1 text-lg font-semibold text-zinc-100">This link has no code</h1>
            <p className="text-sm text-zinc-400">Ask for a fresh link, or open your wallet to see your balance.</p>
          </>
        ) : !sessionReady ? (
          <p className="text-sm text-zinc-400">One moment…</p>
        ) : !user ? (
          <>
            <h1 className="mb-1 text-lg font-semibold text-zinc-100">Claim your Faps</h1>
            <p className="mb-4 text-sm text-zinc-400">
              Your code is waiting. Sign in or make a free account and it lands on your wallet.
            </p>
            <button
              type="button"
              onClick={() => setAuthOpen(true)}
              className="w-full rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"
            >
              Sign in to claim
            </button>
            {authOpen ? (
              <AuthModal
                initialMode="register"
                reason="Faps live on an account — your code lands on it."
                onClose={() => setAuthOpen(false)}
              />
            ) : null}
          </>
        ) : state.t === "busy" || state.t === "idle" ? (
          <p className="text-sm text-zinc-400">Claiming your code…</p>
        ) : state.t === "done" ? (
          <>
            <h1 className="mb-2 flex items-center justify-center gap-1 text-xl font-semibold text-zinc-100">
              <FapsAmount n={state.amount} size={18} /> added
            </h1>
            <p className="mb-4 text-sm text-zinc-400">It&rsquo;s on your wallet. Put it on the wall.</p>
            <button
              type="button"
              onClick={() => {
                trackFapsWallClicked("redeemed", false);
                navigate("/screen?code=0");
              }}
              className="w-full rounded-lg bg-[#f5c518] px-4 py-2.5 text-sm font-semibold text-black hover:brightness-110"
            >
              See the wall
            </button>
          </>
        ) : (
          <>
            <h1 className="mb-1 text-lg font-semibold text-zinc-100">Couldn&rsquo;t claim that</h1>
            <p className="mb-4 text-sm text-red-400">{state.message}</p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="w-full rounded-lg border border-[var(--color-edge)] px-4 py-2.5 text-sm font-medium text-zinc-200 hover:bg-white/10"
            >
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
